import React from 'react';
import { Select, TreeSelect } from 'antd';
import { AdvancedOptions, Comparator, Filter } from '../../../../../../../../../constants/types';
import { COMPARATOR_DESCRIPTION_MAP } from '../../../../../constants';
import { handleFilterSelect } from '../../../../../../utils';
import { EQUAL } from '../../../../../../../../../constants/modifiers';
import { ANY, NIL } from '../../../../../../../../../constants/constants';
import styles from './FilterOptionsSelector.module.scss';

export interface Props {
  readonly filterValue: string;
  readonly filterIndex: number;
  readonly comparator: Comparator;
  readonly advancedOptions: AdvancedOptions;
  readonly selectorOptionValues: Comparator[];
  readonly setComparator: (comparator: Comparator) => void;
  readonly setAdvancedOptions: (advancedOptions: AdvancedOptions) => void;
  readonly modalFilters: Filter[];
  readonly setModalFilters: (modalFilters: Filter[]) => void;
  readonly showAdvancedOptions: boolean;
}

const advancedOptionValues = [
  {
    title: 'derived',
    value: 'derived',
    selectable: false,
    children: [
      {
        value: 'derived_only',
        title: 'derived only',
      },
      {
        value: 'non_derived_only',
        title: 'non derived only',
      },
      {
        value: 'derived_and_non_derived',
        title: 'derived and non derived',
      },
    ],
  },
  {
    title: 'primary/secondary',
    value: 'primary/secondary',
    selectable: false,
    children: [
      {
        value: 'primary_only',
        title: 'primary only',
      },
      {
        value: 'secondary_only',
        title: 'secondary only',
      },
      {
        value: 'primary_and_secondary',
        title: 'primary and secondary',
      },
    ],
  },
];

const FilterOptionsSelector = ({
  filterValue,
  filterIndex,
  comparator,
  advancedOptions,
  selectorOptionValues,
  setComparator,
  modalFilters,
  setModalFilters,
  setAdvancedOptions,
  showAdvancedOptions,
}: Props) => {
  const isNilOrAny = filterValue === ANY || filterValue === NIL;
  const [advancedOptModified, setAdvancedOptModified] = React.useState(false);
  const currAdvancedOptions = modalFilters[filterIndex].advancedOptions;

  const advancedOptionsValues = advancedOptModified
    ? [currAdvancedOptions.derived, currAdvancedOptions.secondary]
    : [];

  const onAdvancedOptionsChange = (selectedVal: string[]) => {
    setAdvancedOptModified(true);
    const newAdvancedOptions = {
      derived: advancedOptions.derived,
      secondary: advancedOptions.secondary,
    };
    selectedVal.forEach((v: string) => {
      if (v === 'derived_only' || v === 'derived_and_non_derived' || v === 'non_derived_only') {
        newAdvancedOptions.derived = v;
      } else if (v === 'secondary_only' || v === 'primary_and_secondary' || v === 'primary_only') {
        newAdvancedOptions.secondary = v;
      }
    });
    setAdvancedOptions(newAdvancedOptions);
    const newModalFilters = handleFilterSelect(
      filterValue,
      filterIndex,
      modalFilters,
      comparator,
      newAdvancedOptions
    );
    setModalFilters(newModalFilters);
  };

  return (
    <div className={styles.comparatorSelector}>
      <Select
        disabled={isNilOrAny}
        dropdownMatchSelectWidth={false}
        onChange={(newComparatorValue: Comparator): void => {
          const updatedModalFilters = handleFilterSelect(
            filterValue,
            filterIndex,
            modalFilters,
            newComparatorValue
          );
          setComparator(newComparatorValue);
          setModalFilters(updatedModalFilters);
        }}
        value={isNilOrAny ? EQUAL : comparator}
      >
        {selectorOptionValues.map(
          (selectorValue: Comparator): JSX.Element => (
            <Select.Option key={selectorValue}>
              {COMPARATOR_DESCRIPTION_MAP[selectorValue]}
            </Select.Option>
          )
        )}
      </Select>
      {showAdvancedOptions && (
        <TreeSelect
          multiple
          treeDefaultExpandAll
          className={styles.advancedOptions}
          dropdownStyle={{ maxHeight: 400, overflow: 'auto' }}
          onChange={onAdvancedOptionsChange}
          placeholder="Advanced Options"
          treeData={advancedOptionValues}
          value={advancedOptionsValues}
        />
      )}
    </div>
  );
};

export default FilterOptionsSelector;
